import { MixConfig } from '../types/config'

import { getFontFaceStyles } from './fonts'

export const formatFontNameToFile = (name: string) => {
  return name.toLowerCase().replaceAll(' ', '-')
}

export const getFontCssObject = (mixConfig: MixConfig) => {
  const fontFamily = mixConfig.frontSettings.fontFamily || ''
  const headingFontFamily = mixConfig.frontSettings.headingFontFamily || ''

  const formattedFontName = formatFontNameToFile(fontFamily)
  const formattedHeadingFontName = formatFontNameToFile(headingFontFamily)

  const uniqueFontNames = Array.from(
    new Set([formattedFontName, formattedHeadingFontName].filter(Boolean))
  )

  const fontFacesStr = uniqueFontNames.map((name) => getFontFaceStyles(name)).join('\n')

  const bodyVar = formattedFontName ? `--f-body: ${formattedFontName}, sans-serif;` : ''
  const headingVar =
    formattedHeadingFontName || formattedFontName
      ? `--f-heading: ${formattedHeadingFontName || formattedFontName}, sans-serif;`
      : ''

  return { fontFacesStr, bodyVar, headingVar }
}

export const SSR_FONTS_STYLE_ID = 'ssr-css-fonts'

export const getFontCssContent = (mixConfig: MixConfig): string => {
  const { fontFacesStr, bodyVar, headingVar } = getFontCssObject(mixConfig)

  if (!fontFacesStr) return ''

  return `${fontFacesStr}
:root {
  ${bodyVar}
  ${headingVar}
}`
}

export const getFontCss = (mixConfig: MixConfig): string => {
  const content = getFontCssContent(mixConfig)

  return content
    ? `
        <style id="${SSR_FONTS_STYLE_ID}">
          ${content}
        </style>
      `
    : ''
}

export const darkenHex = (hex: string, percent: number): string => {
  if (!hex) return ''

  const cleanHex = hex.length === 9 ? hex.slice(0, 7) : hex

  const fullHex = cleanHex.replace(
    /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
    (_, r, g, b) => `#${r}${r}${g}${g}${b}${b}`
  )

  const bigint = parseInt(fullHex.replace('#', ''), 16)
  let r = (bigint >> 16) & 255
  let g = (bigint >> 8) & 255
  let b = bigint & 255

  const factor = 1 - percent / 100
  r = Math.max(0, Math.floor(r * factor))
  g = Math.max(0, Math.floor(g * factor))
  b = Math.max(0, Math.floor(b * factor))

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

export const getAccessibleTextColor = (hexBgColor: string) => {
  const r = parseInt(hexBgColor.slice(1, 3), 16) / 255
  const g = parseInt(hexBgColor.slice(3, 5), 16) / 255
  const b = parseInt(hexBgColor.slice(5, 7), 16) / 255

  const linear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))

  const luminance = 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)

  const whiteContrast = (1 + 0.05) / (luminance + 0.05)
  const blackContrast = (luminance + 0.05) / 0.05

  return whiteContrast >= blackContrast ? '#FFFFFF' : '#000000'
}
